import { noop, useForm } from '@moda/portal-stanchions';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CONFIG } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import { AuthAction } from '../../lib/authenticate';
import { hrefFor } from '../../routers';
import { useCountry } from '../CountrySelector';

const RETURN_SUMMARY_URL = `${CONFIG.MODA_API_URL}/v3.5/return_summary`;

const VALIDATIONS = {
  orderNumber: {
    required: {
      message: 'Please enter your order number.',
      value: true
    }
  },
  guestEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  }
};

export const useGuestRequestReturnForm = (orderNumber: string, guestEmail: string) => {
  const { push } = useHistory();
  const {
    country: { alpha2Code: countryCode }
  } = useCountry();
  const [returnFetchError, setReturnFetchError] = useState(false);
  const [returnFetchLoading, setReturnFetchLoading] = useState(false);
  const { error: authError, handleAuth, loading: authLoading } = useAuth(AuthAction.Register);

  const redirectToReturnPage = useCallback(
    () => push(hrefFor.RequestReturnPage({ orderNumber })),
    [orderNumber, push]
  );

  const fetchReturn = useCallback(
    (accessToken: string) => {
      setReturnFetchLoading(true);
      fetch(`${RETURN_SUMMARY_URL}?order_number=${orderNumber}&country_code=${countryCode}`, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        redirect: 'follow',
        referrer: 'no-referrer',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          authorization: accessToken ? `Bearer ${accessToken}` : ''
        }
      })
        .then(({ ok }) => {
          if (ok) {
            setReturnFetchError(false);

            redirectToReturnPage();
            return;
          }

          setReturnFetchError(true);
        })
        .catch(noop)
        .finally(() => setReturnFetchLoading(false));
    },
    [countryCode, orderNumber, redirectToReturnPage]
  );

  const onSubmit = useCallback(() => {
    handleAuth({
      guest: true,
      email: guestEmail,
      remember: true
    })
      .then(response => {
        if (response?.accessToken) {
          fetchReturn(response.accessToken);

          return;
        }

        setReturnFetchError(true);
      })
      .catch(noop);
  }, [fetchReturn, guestEmail, handleAuth]);

  const { errors: fieldErrors, handleSubmit } = useForm({ onSubmit, validations: VALIDATIONS });

  return {
    fieldErrors,
    handleSubmit,
    loading: authLoading || returnFetchLoading,
    error: authError || returnFetchError
  };
};
